/**
 * B6: Public pricing page at /pricing.
 * 5 tiers: Free €0, Starter €89, Growth €249, Scale €449, Enterprise custom.
 * Annual vs monthly toggle (annual = monthly × 10 = 17% discount).
 * Feature comparison. CTAs → signup. Responsive 768px+.
 */
import { useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "@/i18n";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Plan data
// ---------------------------------------------------------------------------

interface PlanTier {
  key: string;
  nameKey: string;
  descKey: string;
  monthlyPriceCents: number | null; // null = contact sales
  featureKeys: string[]; // i18n keys for feature list
  highlighted?: boolean;
  cta: "getStarted" | "contactSales";
}

const PLANS: PlanTier[] = [
  {
    key: "free",
    nameKey: "pricing.free.name",
    descKey: "pricing.free.desc",
    monthlyPriceCents: 0,
    featureKeys: [
      "pricing.feat.locations_1",
      "pricing.feat.contacts_500",
      "pricing.feat.automations_3",
      "pricing.feat.basic_messaging",
      "pricing.feat.community_support",
    ],
    cta: "getStarted",
  },
  {
    key: "starter",
    nameKey: "pricing.starter.name",
    descKey: "pricing.starter.desc",
    monthlyPriceCents: 8900,
    featureKeys: [
      "pricing.feat.locations_3",
      "pricing.feat.contacts_5k",
      "pricing.feat.automations_25",
      "pricing.feat.sms_email",
      "pricing.feat.ai_assistant",
      "pricing.feat.email_support",
    ],
    cta: "getStarted",
  },
  {
    key: "growth",
    nameKey: "pricing.growth.name",
    descKey: "pricing.growth.desc",
    monthlyPriceCents: 24900,
    featureKeys: [
      "pricing.feat.locations_10",
      "pricing.feat.contacts_25k",
      "pricing.feat.automations_unlimited",
      "pricing.feat.all_channels",
      "pricing.feat.white_label",
      "pricing.feat.custom_domain",
      "pricing.feat.priority_support",
    ],
    highlighted: true,
    cta: "getStarted",
  },
  {
    key: "scale",
    nameKey: "pricing.scale.name",
    descKey: "pricing.scale.desc",
    monthlyPriceCents: 44900,
    featureKeys: [
      "pricing.feat.locations_unlimited",
      "pricing.feat.contacts_100k",
      "pricing.feat.automations_unlimited",
      "pricing.feat.all_channels_api",
      "pricing.feat.advanced_analytics",
      "pricing.feat.sso_roles",
      "pricing.feat.dedicated_support",
    ],
    cta: "getStarted",
  },
  {
    key: "enterprise",
    nameKey: "pricing.enterprise.name",
    descKey: "pricing.enterprise.desc",
    monthlyPriceCents: null,
    featureKeys: [
      "pricing.feat.everything_scale",
      "pricing.feat.contacts_unlimited",
      "pricing.feat.custom_integrations",
      "pricing.feat.sla",
      "pricing.feat.account_manager",
      "pricing.feat.on_premise",
    ],
    cta: "contactSales",
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PricingPage() {
  const { t } = useTranslation();
  const [isAnnual, setIsAnnual] = useState(false);

  function formatPrice(monthlyCents: number | null): string {
    if (monthlyCents === null) return "";
    if (monthlyCents === 0) return "€0";
    const monthly = monthlyCents / 100;
    const price = isAnnual ? Math.round(monthly * 10 / 12) : monthly;
    return `€${price}`;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border px-6 py-4">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <Link to="/" className="text-xl font-bold text-foreground">Ease</Link>
          <div className="flex gap-3">
            <Link to="/login" className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
              {t("auth.login")}
            </Link>
            <Link to="/signup" className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
              {t("auth.signup")}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="px-6 pt-16 pb-8 text-center">
        <h1 className="text-4xl font-bold text-foreground sm:text-5xl">
          {t("pricing.title")}
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">
          {t("pricing.subtitle")}
        </p>

        {/* Billing toggle */}
        <div className="mt-8 flex items-center justify-center gap-3">
          <span className={cn("text-sm font-medium", !isAnnual ? "text-foreground" : "text-muted-foreground")}>
            {t("pricing.monthly")}
          </span>
          <button
            onClick={() => setIsAnnual(!isAnnual)}
            className={cn(
              "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors",
              isAnnual ? "bg-primary" : "bg-muted",
            )}
            role="switch"
            aria-checked={isAnnual}
          >
            <span className={cn(
              "inline-block h-5 w-5 transform rounded-full bg-background shadow transition-transform",
              isAnnual ? "translate-x-5" : "translate-x-0.5",
            )} style={{ marginTop: "2px" }} />
          </button>
          <span className={cn("text-sm font-medium", isAnnual ? "text-foreground" : "text-muted-foreground")}>
            {t("pricing.annual")}
          </span>
          {isAnnual && (
            <span className="rounded-full bg-[var(--color-success)]/10 px-2 py-0.5 text-xs font-medium text-[var(--color-success)]">
              {t("pricing.annualSave")}
            </span>
          )}
        </div>
      </div>

      {/* Plan cards */}
      <div className="mx-auto max-w-7xl px-6 pb-16">
        <div className="grid gap-6 md:grid-cols-3 lg:grid-cols-5">
          {PLANS.map((plan) => (
            <div
              key={plan.key}
              className={cn(
                "relative flex flex-col rounded-xl border p-6",
                plan.highlighted
                  ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5 shadow-lg scale-[1.02] z-10"
                  : "border-border bg-card opacity-95",
              )}
            >
              {plan.highlighted && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                  {t("pricing.popular")}
                </span>
              )}

              <h3 className="text-lg font-semibold text-foreground">{t(plan.nameKey)}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t(plan.descKey)}</p>

              <div className="mt-4">
                {plan.monthlyPriceCents !== null ? (
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-foreground">
                      {formatPrice(plan.monthlyPriceCents)}
                    </span>
                    <span className="text-sm text-muted-foreground">{t("pricing.perMonth")}</span>
                  </div>
                ) : (
                  <span className="text-2xl font-bold text-foreground">Custom</span>
                )}
              </div>

              <ul className="mt-6 flex-1 space-y-2">
                {plan.featureKeys.map((featureKey, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                    <span className="mt-0.5 text-[var(--color-success)]/500">✓</span>
                    {t(featureKey)}
                  </li>
                ))}
              </ul>

              <Link
                to={plan.cta === "contactSales" ? "/contact" : "/signup"}
                className={cn(
                  "mt-6 block rounded-md py-2.5 text-center text-sm font-semibold transition-colors",
                  plan.highlighted
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                    : "border border-border text-foreground hover:bg-accent",
                )}
              >
                {t(plan.cta === "contactSales" ? "pricing.contactSales" : "pricing.getStarted")}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
